import { useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createPublicRocClient } from '@/adapters/publicROCApi';
import { filterConversationMessages } from '@/pages/CRM/ReferralOutbound/ReferralManagement/lib/conversation';
import {
  ReferralMessageModel,
  type ROCMessage
} from '@/pages/CRM/ReferralOutbound/ReferralManagement/models/ReferralMessageModel';
import type { Message } from '@/types/roc/models/Message';
import type { ReadReceipt } from '@/types/roc/models/ReadReceipt';
import { ResponseError } from '@/types/roc/runtime';

import { getPublicReferralMessagesQueryKey } from '../queryKeys';

const POLL_INTERVAL_MS = 5 * 1000;
const MAX_MESSAGES_PAGE_SIZE = 100;

export interface PublicMessagesQueryData {
  messages: ReferralMessageModel[];
  readReceipts: ReadReceipt[];
}

function toRocMessage(message: Message): ROCMessage {
  return {
    ...message,
    receiver_facility_id: message.receiver_facility_id ?? undefined,
    system_event_data:
      (message.system_event_data as Record<string, unknown>) ?? null,
    created_at: message.created_at.toISOString()
  };
}

function shouldStopPolling(error: unknown): boolean {
  return error instanceof ResponseError && error.response.status < 500;
}

function shouldRetry(failureCount: number, error: unknown) {
  if (error instanceof ResponseError) {
    return error.response.status >= 500 && failureCount < 2;
  }

  return failureCount < 2;
}

export function usePublicReferralMessages(
  token: string | undefined,
  referralId: string | undefined
) {
  const queryClient = useQueryClient();
  const cursorRef = useRef<{ key: string; value: string } | null>(null);
  const queryKey = getPublicReferralMessagesQueryKey(token, referralId);
  const cursorKey = queryKey.join('|');

  return useQuery({
    queryKey,
    queryFn: async (): Promise<PublicMessagesQueryData> => {
      if (!token || !referralId) {
        throw new Error('Missing referral context.');
      }

      const activeCursor =
        cursorRef.current?.key === cursorKey ? cursorRef.current.value : null;
      const client = createPublicRocClient(token);
      const response = await client.listMessages({
        referralId,
        limit: MAX_MESSAGES_PAGE_SIZE,
        ...(activeCursor ? { cursor: activeCursor } : {})
      });

      const newMessages = ReferralMessageModel.fromArray(
        response.items.map((item) => toRocMessage(item))
      );
      const readReceipts = response.read_receipts ?? [];

      if (!activeCursor) {
        if (response.cursor) {
          cursorRef.current = { key: cursorKey, value: response.cursor };
        }
        return { messages: newMessages, readReceipts };
      }

      const existing =
        queryClient.getQueryData<PublicMessagesQueryData>(queryKey)?.messages ??
        [];

      if (newMessages.length === 0) {
        return { messages: existing, readReceipts };
      }

      if (response.cursor) {
        cursorRef.current = { key: cursorKey, value: response.cursor };
      }
      return { messages: [...existing, ...newMessages], readReceipts };
    },
    enabled: Boolean(token && referralId),
    retryOnMount: false,
    retry: shouldRetry,
    refetchInterval: (_data, query) =>
      shouldStopPolling(query.state.error) ? false : POLL_INTERVAL_MS,
    refetchIntervalInBackground: false
  });
}

export function usePublicSendReferralMessageMutation(
  token: string | undefined,
  referralId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (content: string) => {
      if (!token || !referralId) {
        throw new Error('Missing referral context.');
      }

      const client = createPublicRocClient(token);
      return client.createMessage({
        referralId,
        createMessageRequest: { content }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getPublicReferralMessagesQueryKey(token, referralId)
      });
    }
  });
}

export function usePublicReferralConversation(
  messages: ReferralMessageModel[] | undefined,
  senderFacilityId: string | undefined,
  recipientFacilityId: string | undefined
) {
  return useMemo(() => {
    if (!messages || !senderFacilityId || !recipientFacilityId) {
      return [];
    }

    return filterConversationMessages(
      messages,
      senderFacilityId,
      recipientFacilityId
    );
  }, [messages, recipientFacilityId, senderFacilityId]);
}
